import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Avatar,
    IconButton,
    Breadcrumbs,
    Link,
} from '@mui/material';
import {
    Instagram as InstagramIcon,
    ChevronRight as ChevronRightIcon,
    SwipeLeft as SwipeLeftIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import TikTokIcon from '../components/icons/TikTokIcon';
import ShortFormDetailLayout from '../components/ShortFormDetailLayout';
import apiService from '../services/api';
import { ROUTES_NAMES } from '../components/Layout';
import { useBrandExplorerStore } from '../stores/brandExplorerStore';
import { useTrackerStore } from '../stores/trackerStore';
import { useAuth } from '../contexts/AuthContext';
import { productBrand } from '../productBrand';
import { getS3Url, ensureDirectS3Url } from '../utils/imageUtils';

interface BrandExplorerCreatorDetailProps {
    fixedBrandId?: string;
}

const BrandExplorerCreatorDetail: React.FC<BrandExplorerCreatorDetailProps> = ({ fixedBrandId }) => {
    const { creatorId, platform: platformParam } = useParams<{ creatorId?: string; platform?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { selectedBrand, startDate: explorerStartDate, endDate: explorerEndDate } = useBrandExplorerStore();
    const { startDate: trackerStartDate, endDate: trackerEndDate } = useTrackerStore();
    const { getCompanyTrackerBrandId } = useAuth();

    const isTracker = location.pathname.startsWith('/tracker');
    const TONYBET_BRAND_ID = '68ab4884dfd4c581cbe669bb';

    const isEnglish = !!fixedBrandId;

    const brandIdParam = searchParams.get('brandId');
    const platform = searchParams.get('platform') as 'yt' | 'ig' | null;
    const platformFromQuery = searchParams.get('platform');
    const startDateParam = isTracker ? trackerStartDate : (searchParams.get('startDate') || explorerStartDate);
    const endDateParam = isTracker ? trackerEndDate : (searchParams.get('endDate') || explorerEndDate);

    const brandId = fixedBrandId || (isTracker ? getCompanyTrackerBrandId() : null) || selectedBrand?._id || brandIdParam || null;

    // Sorting state - default to date descending (newest first)
    const [orderBy, setOrderBy] = useState<'publishedAt' | 'likes' | 'comments' | 'views' | 'title' | 'interactions'>('publishedAt');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');

    // Path has platform for tracker and TonyBet (/tracker/:platform/:creatorId and /tonybet/:platform/:creatorId); query fallback for brand-explorer
    const apiPlatform =
        (platformParam === 'instagram' || platformParam === 'tiktok')
            ? platformParam
            : (platformFromQuery === 'instagram' || platformFromQuery === 'tiktok')
                ? platformFromQuery
                : undefined;
    const { data, isLoading, error } = useQuery({
        queryKey: ['brand-creator-detail', brandId, creatorId, undefined, apiPlatform, startDateParam, endDateParam, orderBy, order],
        queryFn: () => apiService.getBrandCreatorDetail(
            brandId!,
            creatorId!,
            undefined,
            undefined,
            startDateParam,
            endDateParam,
            apiPlatform,
            orderBy,
            order
        ),
        enabled: !!brandId && !!creatorId,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

    // Handle sort request: backend returns sorted data, so we just update state and refetch
    const handleRequestSort = (property: typeof orderBy) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Reels come sorted from backend (no client-side filter/sort)
    const displayReels = data?.reels ?? [];

    // Format number with commas
    const formatNumber = (num: number): string => {
        return new Intl.NumberFormat('es-ES').format(num);
    };

    const handleBack = () => {
        const baseRoute = fixedBrandId
            ? ROUTES_NAMES.TONYBET_EXPLORER_YEAR
            : isTracker
                ? ROUTES_NAMES.TRACKER
                : ROUTES_NAMES.TONYBET_EXPLORER_YEAR;
        if (fixedBrandId || isTracker) {
            navigate(baseRoute);
        } else {
            const params = new URLSearchParams();
            if (brandId) params.set('brandId', brandId);
            if (startDateParam) params.set('startDate', startDateParam);
            if (endDateParam) params.set('endDate', endDateParam);
            if (platform) params.set('platform', platform);
            const queryString = params.toString();
            navigate(queryString ? `${baseRoute}?${queryString}` : baseRoute);
        }
    };

    if (isLoading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error || !data) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    Error al cargar los detalles del creador. Por favor, intenta de nuevo.
                </Alert>
            </Container>
        );
    }

    const { brand, creator, statistics } = data;

    // Use backend-provided statistics only (no client-side aggregation)
    const totalReels = statistics?.totalReels ?? 0;
    const totalViews = statistics?.totalViews ?? 0;
    const totalLikes = statistics?.totalLikes ?? 0;
    const totalComments = statistics?.totalComments ?? 0;

    return (
        <>
            {/* Podscope Header - Only show for public route */}
            {fixedBrandId && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '64px',
                        display: 'flex',
                        alignItems: 'center',
                        px: { xs: 2, sm: 3 },
                        backgroundColor: '#ffffff',
                        zIndex: 1100,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
                        <Typography
                            component="h1"
                            variant="h1"
                            onClick={() => window.open(productBrand.marketingUrl, '_blank', 'noopener,noreferrer')}
                            sx={{
                                fontSize: '1.5rem',
                                fontWeight: 800,
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': {
                                    opacity: 0.8,
                                },
                            }}
                        >
                            {productBrand.name}
                        </Typography>
                    </Container>
                </Box>
            )}
            <ShortFormDetailLayout
                onBack={handleBack}
                avatar={
                    creator.profilePicS3Key ? (
                        <Avatar src={ensureDirectS3Url(getS3Url(creator.profilePicS3Key))} sx={{ width: 56, height: 56 }} />
                    ) : creator.profilePicUrl ? (
                        <Avatar src={creator.profilePicUrl} sx={{ width: 56, height: 56 }} />
                    ) : (
                        <Avatar sx={{ width: 56, height: 56 }}>{creator.platform === 'tiktok' ? <TikTokIcon /> : <InstagramIcon />}</Avatar>
                    )
                }
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="h1" component="h1" sx={{ fontSize: '2rem', fontWeight: 600, mb: 0.1 }}>
                            @{creator.username}
                        </Typography>
                        {creator.isVerified && (
                            creator.platform === 'tiktok'
                                ? <TikTokIcon sx={{ color: 'primary.main', fontSize: '1.25rem' }} />
                                : <InstagramIcon color="primary" fontSize="small" />
                        )}
                    </Box>
                }
                subtitle={
                    <>
                        {creator.fullName && (
                            <Typography variant="body1" color="text.secondary">{creator.fullName}</Typography>
                        )}
                        {creator.followersCount && (
                            <Typography variant="body2" color="text.secondary">
                                {formatNumber(creator.followersCount)} seguidores
                            </Typography>
                        )}
                    </>
                }
                breadcrumbs={
                    !fixedBrandId && !isTracker ? (
                        <Breadcrumbs aria-label="breadcrumb" separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 2 }}>
                            <Link component={RouterLink} to={isTracker ? ROUTES_NAMES.TRACKER : ROUTES_NAMES.TONYBET_EXPLORER_YEAR} underline="hover" color="inherit">
                                {isTracker ? 'Tracker' : 'TonyBet'}
                            </Link>
                            {brand && (
                                <Link component={RouterLink} to={`${isTracker ? ROUTES_NAMES.TRACKER : ROUTES_NAMES.TONYBET_EXPLORER_YEAR}?brandId=${brandId}`} underline="hover" color="inherit">
                                    {brand.name}
                                </Link>
                            )}
                            {startDateParam && endDateParam && !isTracker && (
                                <Link component={RouterLink} to={`${isTracker ? ROUTES_NAMES.TRACKER : ROUTES_NAMES.TONYBET_EXPLORER_YEAR}?brandId=${brandId}&startDate=${startDateParam}&endDate=${endDateParam}`} underline="hover" color="inherit">
                                    {startDateParam} – {endDateParam}
                                </Link>
                            )}
                            {creator && <Typography color="text.primary">@{creator.username}</Typography>}
                        </Breadcrumbs>
                    ) : undefined
                }
                resumenTitle={isEnglish ? 'Summary' : 'Resumen'}
                cards={[
                    { value: totalReels, label: 'Reels' },
                    { value: totalViews, label: 'Views' },
                    { value: totalLikes, label: 'Likes' },
                    { value: totalComments, label: isEnglish ? 'Comments' : 'Comentarios' },
                ]}
                formatNumber={formatNumber}
                contentTitle={creator.platform === 'tiktok' ? 'TikTok Videos' : 'Reels de Instagram'}
                contentChildren={
                    displayReels.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Box
                                sx={{
                                    display: { xs: 'flex', md: 'none' },
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    py: 1,
                                    color: 'text.secondary',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <SwipeLeftIcon />
                                <Typography variant="caption">Desliza para ver más</Typography>
                            </Box>
                            <Table sx={{ tableLayout: 'fixed' }}>
                                <TableHead>
                                    <TableRow>
                                        {/* Content - left aligned, hidden on mobile */}
                                        <TableCell align="left" sx={{ width: '40%', display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                            <TableSortLabel
                                                active={orderBy === 'title'}
                                                direction={orderBy === 'title' ? order : 'asc'}
                                                onClick={() => handleRequestSort('title')}
                                            >
                                                Content
                                            </TableSortLabel>
                                        </TableCell>
                                        {/* Published - right aligned, hidden on mobile */}
                                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                            <TableSortLabel
                                                active={orderBy === 'publishedAt'}
                                                direction={orderBy === 'publishedAt' ? order : 'desc'}
                                                onClick={() => handleRequestSort('publishedAt')}
                                            >
                                                Published
                                            </TableSortLabel>
                                        </TableCell>
                                        {/* Interactions - right aligned, visible only on mobile */}
                                        <TableCell align="right" sx={{ display: { xs: 'table-cell', sm: 'none' }, padding: { xs: '12px', sm: '16px' } }}>
                                            <TableSortLabel
                                                active={orderBy === 'interactions'}
                                                direction={orderBy === 'interactions' ? order : 'desc'}
                                                onClick={() => handleRequestSort('interactions')}
                                            >
                                                Interactions
                                            </TableSortLabel>
                                        </TableCell>
                                        {/* Views - right aligned */}
                                        <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                            <TableSortLabel
                                                active={orderBy === 'views'}
                                                direction={orderBy === 'views' ? order : 'asc'}
                                                onClick={() => handleRequestSort('views')}
                                            >
                                                Views
                                            </TableSortLabel>
                                        </TableCell>
                                        {/* Likes - right aligned, hidden on mobile */}
                                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                            <TableSortLabel
                                                active={orderBy === 'likes'}
                                                direction={orderBy === 'likes' ? order : 'asc'}
                                                onClick={() => handleRequestSort('likes')}
                                            >
                                                Likes
                                            </TableSortLabel>
                                        </TableCell>
                                        {/* Comments - right aligned, hidden on mobile */}
                                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                            <TableSortLabel
                                                active={orderBy === 'comments'}
                                                direction={orderBy === 'comments' ? order : 'asc'}
                                                onClick={() => handleRequestSort('comments')}
                                            >
                                                Comments
                                            </TableSortLabel>
                                        </TableCell>
                                        {/* Link - right aligned, small width */}
                                        <TableCell align="right" sx={{ width: '50px', padding: { xs: '8px', sm: '12px' } }}>
                                            Link
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayReels.map((mention) => {
                                        const content = mention.content;

                                        return (
                                            <TableRow key={mention._id} hover>
                                                <TableCell sx={{ width: '40%', overflow: 'hidden', display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }} align="left">
                                                    <Box sx={{ width: '100%', overflow: 'hidden' }}>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                width: '100%',
                                                                display: 'block'
                                                            }}
                                                        >
                                                            {content.title}
                                                        </Typography>
                                                        {mention.context && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    width: '100%',
                                                                    display: 'block'
                                                                }}
                                                            >
                                                                {mention.context}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                    <Typography variant="caption">
                                                        {new Date(content.publishedAt).toLocaleDateString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ display: { xs: 'table-cell', sm: 'none' }, padding: { xs: '12px', sm: '16px' } }}>
                                                    <Typography variant="body2">
                                                        {formatNumber((content.likes || 0) + (content.comments || 0))}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                    <Typography variant="body2">
                                                        {formatNumber(content.views || 0)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                    <Typography variant="body2">
                                                        {formatNumber(content.likes || 0)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                    <Typography variant="body2">
                                                        {formatNumber(content.comments || 0)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ width: '50px', padding: { xs: '8px', sm: '12px' } }}>
                                                    {content.url ? (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => window.open(content.url!, '_blank')}
                                                            sx={{ color: 'primary.main' }}
                                                            title="Open link"
                                                        >
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">
                                                            No link
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Alert severity="info">
                            No se encontraron reels para este creador.
                        </Alert>
                    )
                }
                containerTopMargin={fixedBrandId ? { xs: 10, sm: 11, md: 12 } : 4}
            />
        </>
    );
};

export default BrandExplorerCreatorDetail;

